/**
 * Office Review — Repairs. Review had no repair readback at all: a repair
 * saved without photos (or recorded by the office for the tech) was invisible
 * until someone opened the report. Mockup region 3,
 * mockups/repair-without-photos.html (approved 2026-10-01).
 */
import { Wrench } from "lucide-react";

export interface ReviewRepairRow {
    id: string;
    asset_id: string;
    repair_notes: string | null;
    after_photos?: Record<string, string[]> | null;
    photos_skipped_reason?: string | null;
    performed_by_name?: string | null;
    entered_by_name?: string | null;
    serial_number?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    size_label?: string | null;
    created_at: string;
}

function deviceName(r: ReviewRepairRow): string {
    const parts = [r.manufacturer, r.model, r.size_label].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Device";
}

export function OfficeRepairsSection({ rows }: { rows: ReviewRepairRow[] }) {
    if (rows.length === 0) return null;
    return (
        <section data-testid="office-repairs">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-foreground">
                <Wrench className="h-4 w-4" /> Repairs ({rows.length})
            </h3>
            <div className="space-y-3">
                {rows.map((r) => {
                    const photos = Object.values(r.after_photos ?? {})
                        .flat()
                        .filter(Boolean);
                    const typedBy =
                        r.entered_by_name?.trim() && r.entered_by_name.trim() !== r.performed_by_name
                            ? r.entered_by_name.trim()
                            : null;
                    return (
                        <div key={r.id} className="rounded-md border bg-muted/30 p-3 text-xs space-y-1.5">
                            <div className="flex flex-wrap items-baseline gap-2">
                                <span className="text-sm font-medium">{deviceName(r)}</span>
                                {r.serial_number && (
                                    <span className="font-mono text-foreground/60">S/N {r.serial_number}</span>
                                )}
                            </div>
                            <div className="text-foreground/70">
                                Repaired by{" "}
                                <span className="font-medium text-foreground/90">
                                    {r.performed_by_name || "unknown"}
                                </span>
                                {typedBy && <> · entered by {typedBy}</>} ·{" "}
                                {new Date(r.created_at).toLocaleDateString()}
                                {photos.length > 0 && (
                                    <>
                                        {" "}
                                        · {photos.length} photo{photos.length === 1 ? "" : "s"}
                                    </>
                                )}
                            </div>
                            {r.repair_notes?.trim() ? (
                                <div className="rounded bg-background/70 px-2 py-1 whitespace-pre-wrap text-foreground/80">
                                    {r.repair_notes}
                                </div>
                            ) : (
                                <div className="italic text-amber-600">no repair notes</div>
                            )}
                            {r.photos_skipped_reason && (
                                <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-foreground/80">
                                    <span className="font-semibold text-amber-700">No repair photos.</span>{" "}
                                    {r.photos_skipped_reason}
                                </div>
                            )}
                            {photos.length > 0 && (
                                <div className="grid grid-cols-6 gap-1.5 max-w-xs">
                                    {photos.map((src, i) => (
                                        <a
                                            key={i}
                                            href={src}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block aspect-square overflow-hidden rounded border bg-muted"
                                        >
                                            <img
                                                src={src}
                                                alt={`Repair photo ${i + 1}`}
                                                className="h-full w-full object-cover"
                                            />
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
